"use client"

import { Head, router } from "@inertiajs/react"
import { SearchLg, Users01 } from "@untitledui/icons"
import { useEffect, useRef, useState } from "react"

import { UsersTable } from "@/components/admin/users/users-table"
import { EmptyState } from "@/components/ui/empty-state"
import { Input } from "@/components/ui/input"
import { PaginationCardMinimal } from "@/components/ui/pagination"
import { Select } from "@/components/ui/select"
import { DashboardLayout } from "@/layouts/dashboard-layout"
import { adminUsersPath } from "@/routes"
import type { UserRow } from "@/types"
import type { Pagination } from "@/types/contents/pagination"

interface Filters {
  q: string | null
  status: string
  role: string
}

interface IndexProps {
  users: UserRow[]
  pagination: Pagination
  filters: Filters
}

const STATUS_ITEMS = [
  { id: "all", label: "All statuses" },
  { id: "active", label: "Active" },
  { id: "suspended", label: "Suspended" },
  { id: "deleted", label: "Deleted" },
]

const ROLE_ITEMS = [
  { id: "all", label: "Everyone" },
  { id: "admin", label: "Admins" },
]

export default function UsersIndex({ users, pagination, filters }: IndexProps) {
  const [query, setQuery] = useState(filters.q ?? "")

  const visit = (params: Partial<Filters> & { page?: number }) => {
    router.get(
      adminUsersPath(),
      {
        q: params.q ?? filters.q ?? undefined,
        status: params.status ?? filters.status,
        role: params.role ?? filters.role,
        page: params.page ?? 1,
      },
      { preserveState: true, replace: true },
    )
  }

  // Debounced search — fires only once typing pauses, and never on mount.
  const isFirstRender = useRef(true)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    const timeout = setTimeout(() => visit({ q: query || undefined }), 300)
    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  const isFiltered =
    Boolean(filters.q) || filters.status !== "all" || filters.role !== "all"

  return (
    <DashboardLayout>
      <Head title="Users" />
      <main className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 pt-8 pb-12 lg:px-8">
        <header className="flex flex-col gap-0.5">
          <h1 className="text-primary font-display md:text-display-xs text-xl font-semibold">
            Users
          </h1>
          <p className="text-md text-tertiary">
            The whole herd — review accounts, contributions, and support
            actions.
          </p>
        </header>

        <div className="flex flex-col gap-3 sm:flex-row">
          <Input
            size="sm"
            icon={SearchLg}
            aria-label="Search users"
            placeholder="Search by name or email"
            value={query}
            onChange={setQuery}
            className="sm:max-w-xs"
          />
          <div className="flex gap-3">
            <Select
              size="sm"
              aria-label="Filter by status"
              items={STATUS_ITEMS}
              selectedKey={filters.status}
              onChange={(key) => key != null && visit({ status: String(key) })}
              className="w-40"
            >
              {(item) => (
                <Select.Item key={item.id} id={item.id}>
                  {item.label}
                </Select.Item>
              )}
            </Select>
            <Select
              size="sm"
              aria-label="Filter by role"
              items={ROLE_ITEMS}
              selectedKey={filters.role}
              onChange={(key) => key != null && visit({ role: String(key) })}
              className="w-36"
            >
              {(item) => (
                <Select.Item key={item.id} id={item.id}>
                  {item.label}
                </Select.Item>
              )}
            </Select>
          </div>
        </div>

        {users.length === 0 ? (
          <EmptyState size="sm" className="py-10">
            <EmptyState.Header pattern="none">
              <EmptyState.FeaturedIcon
                color="gray"
                theme="modern"
                icon={Users01}
              />
            </EmptyState.Header>
            <EmptyState.Content>
              <p className="text-primary text-md text-center font-semibold">
                {isFiltered ? "No users match" : "No users yet"}
              </p>
              <EmptyState.Description>
                {isFiltered
                  ? "Try a different search or clear the filters."
                  : "Accounts appear here as people join."}
              </EmptyState.Description>
            </EmptyState.Content>
          </EmptyState>
        ) : (
          <UsersTable
            rows={users}
            aria-label="Users"
            footer={
              pagination.total > 1 ? (
                <PaginationCardMinimal
                  page={pagination.page}
                  total={pagination.total}
                  onPageChange={(page) => visit({ page })}
                />
              ) : undefined
            }
          />
        )}
      </main>
    </DashboardLayout>
  )
}
